// Modified GameCoordinator.js with optimized listeners and data handling
import useConfirm from "../Components/Modals/useConfirm";

import React, { useState, useEffect } from "react";
import PlayGame from "./PlayGame";
import RoundResults from "./RoundResults";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  limit,
  deleteField,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ReviewPhase from "./ReviewPhase";
import { statsService } from "../Services/statisticsService";
import FinalResults from "./FinalResults";
import ChatContainer from "../Components/Chat/ChatContainer";
import { systemMessageService } from "../Services/systemMessageService";
import { chatService } from "../Services/chatServices";
import LetterPicker from "../Components/LetterPicker";
import { PencilMascot } from "../Components/Mascot";
import LeaveGameButton from "../Components/LeaveGameButton";
import HostControlPanel from "../Components/HostControlPanel";
import analytics from "../Services/analyticsService";

const GameCoordinator = () => {
  const [confirmDialog, ask] = useConfirm();
  const [roomData, setRoomData] = useState(null);
  const [roomDocId, setRoomDocId] = useState(null);
  const [currentRound, setCurrentRound] = useState(1);
  const [loading, setLoading] = useState(true);
  const [gamePhase, setGamePhase] = useState("playing");
  const [letterPickingStartTime, setLetterPickingStartTime] = useState(null);
  const [showHostSkipWaiting, setShowHostSkipWaiting] = useState(false);

  const currentUserId = localStorage.getItem("currentUserId");
  const currentRoomId = localStorage.getItem("currentRoomId");
  const isHost = roomData?.hostId === currentUserId;
  const navigate = useNavigate();
  const [showLetterPicker, setShowLetterPicker] = useState(false);
  const [isLetterPickingPhase, setIsLetterPickingPhase] = useState(false);
  const [currentLetterPicker, setCurrentLetterPicker] = useState(null);

  // Add this temporarily to your GameCoordinator to see the actual state
  // console.log("Room gameStatus:", roomData?.gameStatus);
  // console.log(
  //   "Current phase from chatService:",
  //   chatService.getCurrentChatPhase(roomData)
  // );
  // The scroll position carries over from the previous screen, so a phone
  // opened the results part-way down with the headline out of sight.
  useEffect(() => {
    if (gamePhase === "results" || gamePhase === "finished") window.scrollTo(0, 0);
  }, [gamePhase]);

  // Store minimal game state in localStorage for quick recovery
  useEffect(() => {
    if (roomData && roomData.gameStatus) {
      const minimalState = {
        gameStatus: roomData.gameStatus,
        currentRound: roomData.currentRound,
        currentLetter: roomData.currentLetter,
      };
      localStorage.setItem("gameState", JSON.stringify(minimalState));
    }
  }, [roomData]);

  useEffect(() => {
    if (isLetterPickingPhase && !showLetterPicker) {
      // We're waiting for someone else to pick
      setLetterPickingStartTime(Date.now());
      setShowHostSkipWaiting(false);

      const timer = setTimeout(() => {
        if (isHost) {
          setShowHostSkipWaiting(true);
        }
      }, 20000); // Show skip button after 20 seconds

      return () => clearTimeout(timer);
    } else {
      setLetterPickingStartTime(null);
      setShowHostSkipWaiting(false);
    }
  }, [isLetterPickingPhase, showLetterPicker, isHost]);

  useEffect(() => {
    if (!currentRoomId) {
      navigate("/");
      return;
    }

    // First, get the document ID for direct reference
    const getDocId = async () => {
      const q = query(
        collection(db, "rooms"),
        where("roomId", "==", currentRoomId),
        limit(1)
      );

      try {
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docId = querySnapshot.docs[0].id;
          setRoomDocId(docId);

          // Now set up a more efficient listener on the specific document
          const unsubscribe = onSnapshot(
            doc(db, "rooms", docId),
            (docSnapshot) => {
              if (docSnapshot.exists()) {
                const data = { id: docSnapshot.id, ...docSnapshot.data() };
                setRoomData(data);

                // Process game state more efficiently
                processGameState(data);

                setLoading(false);
              } else {
                // Document no longer exists
                console.log("Room no longer exists");
                setLoading(false);
                navigate("/");
              }
            }
          );

          return unsubscribe;
        } else {
          console.log("Room not found");
          setLoading(false);
          navigate("/");
        }
      } catch (error) {
        console.error("Error getting room:", error);
        setLoading(false);
      }
    };

    const unsubscribePromise = getDocId();

    // Cleanup function
    return () => {
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [currentRoomId, navigate]);

  // More efficient game state processing
  const processGameState = (data) => {
    if (
      isHost &&
      data.selectedLetter &&
      data.letterSelectedBy &&
      data.isLetterPickingPhase
    ) {
      // A non-host player selected a letter, host should process it
      handleLetterSelected(data.selectedLetter, data.isAutoSelected || false);
      return;
    }

    // DELETE_FLAG: Check for letter picking phase FIRST
    if (
      data.isLetterPickingPhase &&
      data.hasLetterPicker &&
      data.letterPickerTurnOrder
    ) {
      const currentPickerIndex = data.currentLetterPickerIndex || 0;
      const currentPicker = data.letterPickerTurnOrder[currentPickerIndex];

      if (currentPicker && currentPicker.userId === currentUserId) {
        // It's this player's turn to pick
        setCurrentLetterPicker(currentPicker);
        setIsLetterPickingPhase(true);
        setShowLetterPicker(true);
      } else if (currentPicker) {
        // Someone else is picking
        setCurrentLetterPicker(currentPicker);
        setIsLetterPickingPhase(true);
        setShowLetterPicker(false);
      }

      return;
    }

    if (!data.isLetterPickingPhase) {
      setIsLetterPickingPhase(false);
      setShowLetterPicker(false);
      setCurrentLetterPicker(null);
    }

    const currentRound = data.currentRound || 1;
    const roundData = data.rounds?.[currentRound];

    if (data.gameStatus === "ended" || !roundData) {
      setGamePhase("finished");
    } else if (roundData.reviewCompleted && roundData.isComplete) {
      // Round is completely finished, check if we should move to next round
      checkForNextRound(data);
    } else if (
      roundData.submissions?.length === data.players?.length ||
      roundData.timeExpired
    ) {
      // All players submitted or time expired, move to review
      setGamePhase("reviewing");
    } else if (roundData.reviewCompleted && !roundData.isComplete) {
      // Review completed, show results
      setGamePhase("results");
    } else {
      // Still in playing phase
      setGamePhase("playing");
    }

    setCurrentRound(currentRound);
  };

  const checkForNextRound = async (data) => {
    if (data.gameStatus === "ended") {
      setGamePhase("finished");
      return;
    }

    // Check if all letters have been played
    const playedLetters = data.playedLetters || [];
    if (playedLetters.length >= 26) {
      setGamePhase("finished");
      return;
    }

    // If we're the host, we can start the next round
    if (isHost) {
      setGamePhase("results");
    } else {
      // Non-hosts wait for results screen
      setGamePhase("results");
    }
  };

  const startNextRound = async () => {
    if (!isHost || !roomData || !roomDocId) return;

    try {
      const playedLetters = roomData.playedLetters || [];
      const nextLetter = generateRandomLetter(playedLetters);

      if (!nextLetter) {
        // Game is finished - all letters used
        await updateDoc(doc(db, "rooms", roomDocId), {
          gameStatus: "finished",
        });

        try {
          const standings = getFinalStandings();
          const winner = standings[0]?.name || "Unknown";
          const totalRounds = roomData.currentRound || 0;
          await systemMessageService.gameEnded(roomDocId, winner, totalRounds);
        } catch (error) {
          console.warn("Failed to send game end message:", error);
        }

        const gameStartedAt = roomData?.rounds?.[1]?.startedAt?.toDate?.() ?? null;
        analytics.track("game_completed", {
          room_id: roomDocId,
          total_rounds: roomData.currentRound || 0,
          duration_sec: gameStartedAt ? Math.round((Date.now() - gameStartedAt.getTime()) / 1000) : 0,
          end_reason: "natural",
        });

        setGamePhase("finished");
        return;
      }

      const nextRound = (roomData.currentRound || 1) + 1;
      const roundStartTime = new Date(Date.now() + 5000); // 5 second delay

      // 🔥 SPEED MODE LOGIC - Last 6 rounds get 20 seconds!
      const remainingLetters = 26 - playedLetters.length;
      const isSpeedMode = remainingLetters <= 6;
      const timeLimit = isSpeedMode ? 20 : 45;

      // Clean up previous round data to save space
      if (roomData.rounds && roomData.rounds[currentRound]) {
        await updateDoc(doc(db, "rooms", roomDocId), {
          [`rounds.${currentRound}.submissions`]: deleteField(),
          [`rounds.${currentRound}.reviewVotes`]: deleteField(),
          [`rounds.${currentRound}.submissions`]: deleteField(),
          [`rounds.${currentRound}.playersSubmitted`]: deleteField(),
        });
      }

      await updateDoc(doc(db, "rooms", roomDocId), {
        currentRound: nextRound,
        currentLetter: nextLetter,
        playedLetters: [...playedLetters, nextLetter],
        roundItemUsage: {}, // reset power-up usage for new round
        [`rounds.${nextRound}`]: {
          letter: nextLetter,
          startedAt: roundStartTime,
          timeLimit: timeLimit, // 🔥 Use dynamic time limit here!
          isSpeedMode: isSpeedMode, // Flag for UI
          submissions: [],
          isComplete: false,
          reviewCompleted: false,
        },
      });
      try {
        if (isSpeedMode) {
          await systemMessageService.speedModeActivated(
            roomDocId,
            remainingLetters
          );
        }
        await systemMessageService.nextRoundStarting(
          roomDocId,
          nextRound,
          nextLetter,
          5
        );
      } catch (error) {
        console.warn("Failed to send round messages:", error);
      }

      const specialRound = (roomData.specialRounds || []).find((r) => r.round === nextRound);
      analytics.track("round_started", {
        room_id: roomDocId,
        round_number: nextRound,
        letter: nextLetter,
        is_special_round: !!specialRound,
        ...(specialRound ? { special_round_type: specialRound.type } : {}),
      });

      // Brief delay then move to playing phase
      setTimeout(() => {
        setGamePhase("playing");
      }, 5000);
    } catch (error) {
      console.error("Error starting next round:", error);
    }
  };

  const generateRandomLetter = (playedLetters = []) => {
    const allLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const availableLetters = allLetters.filter(
      (letter) => !playedLetters.includes(letter)
    );

    if (availableLetters.length === 0) return null;

    const randomIndex = Math.floor(Math.random() * availableLetters.length);
    return availableLetters[randomIndex];
  };

  const startNextRoundWithPicker = async () => {
    if (!isHost || !roomData || !roomDocId) return;

    try {
      const playedLetters = roomData.playedLetters || [];
      const availableLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
        .split("")
        .filter((letter) => !playedLetters.includes(letter));

      if (availableLetters.length === 0) {
        await updateDoc(doc(db, "rooms", roomDocId), {
          gameStatus: "finished",
        });
        setGamePhase("finished");
        return;
      }

      const nextRound = (roomData.currentRound || 1) + 1;

      if (roomData.hasLetterPicker && roomData.letterPickerTurnOrder) {
        const currentPickerIndex = roomData.currentLetterPickerIndex || 0;
        const currentPicker =
          roomData.letterPickerTurnOrder[currentPickerIndex];

        // DELETE_FLAG: Set letter picking phase in Firebase
        await updateDoc(doc(db, "rooms", roomDocId), {
          isLetterPickingPhase: true,
          currentLetterPickerIndex: currentPickerIndex,
          nextRoundNumber: nextRound,
        });

        return;
      }

      const nextLetter = generateRandomLetter(playedLetters);
      await proceedWithSelectedLetter(nextLetter, nextRound, "random");
    } catch (error) {
      console.error("Error starting next round:", error);
    }
  };

  const handleLetterSelected = async (selectedLetter, isAutoSelected) => {
    // DELETE_FLAG: Allow current letter picker OR host to handle selection
    const isCurrentPicker = currentLetterPicker?.userId === currentUserId;

    if ((!isHost && !isCurrentPicker) || !roomData || !roomDocId) return;

    try {
      const nextRound =
        roomData.nextRoundNumber || (roomData.currentRound || 1) + 1;

      // Close letter picker locally
      setShowLetterPicker(false);
      setIsLetterPickingPhase(false);

      // DELETE_FLAG: If not host, let host handle the Firebase updates
      if (!isHost) {
        // Non-host picker: just send the selection to Firebase for host to process
        await updateDoc(doc(db, "rooms", roomDocId), {
          selectedLetter: selectedLetter,
          letterSelectedBy: currentUserId,
          letterSelectionTime: new Date(),
          isAutoSelected: isAutoSelected,
        });
        return;
      }

      // Host logic: process the letter selection
      const letterToUse = selectedLetter || roomData.selectedLetter;
      const wasAutoSelected = isAutoSelected || roomData.isAutoSelected;

      // Update turn order for next round
      const nextPickerIndex =
        ((roomData.currentLetterPickerIndex || 0) + 1) %
        roomData.letterPickerTurnOrder.length;

      // Clear letter picking phase and update turn order
      await updateDoc(doc(db, "rooms", roomDocId), {
        isLetterPickingPhase: false,
        currentLetterPickerIndex: nextPickerIndex,
        nextLetterPickerIndex:
          (nextPickerIndex + 1) % roomData.letterPickerTurnOrder.length,
        nextRoundNumber: null,
        // DELETE_FLAG: Clear selection fields
        selectedLetter: null,
        letterSelectedBy: null,
        letterSelectionTime: null,
        isAutoSelected: null,
      });

      // Proceed with selected letter
      await proceedWithSelectedLetter(
        letterToUse,
        nextRound,
        wasAutoSelected ? "auto" : "picked"
      );
    } catch (error) {
      console.error("Error handling letter selection:", error);
    }
  };

  const handleHostSkipFromWaiting = () => {
    if (!isHost || !roomData || !roomDocId) return;

    const availableLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
      .split("")
      .filter((letter) => !(roomData.playedLetters || []).includes(letter));

    const randomLetter =
      availableLetters[Math.floor(Math.random() * availableLetters.length)];

    // Simulate auto-selection
    handleLetterSelected(randomLetter, true);
  };

  const proceedWithSelectedLetter = async (letter, round, source) => {
    if (!roomDocId || !roomData) return;

    const roundStartTime = new Date(Date.now() + 5000); // 5 second delay
    const remainingLetters = 26 - (roomData.playedLetters?.length || 0);
    const isSpeedMode = remainingLetters <= 6;

    // DELETE_FLAG: Determine time limit with proper priority
    let timeLimit = 45; // Default fallback

    if (isSpeedMode) {
      // Speed mode overrides everything
      timeLimit = 20;
    } else if (roomData.hasCustomTimeLimit && roomData.customTimeLimit) {
      // Use custom time limit if available
      timeLimit = roomData.customTimeLimit;
    }

    console.log(
      `🕒 Setting round ${round} time limit to ${timeLimit}s (speedMode: ${isSpeedMode}, custom: ${roomData.customTimeLimit})`
    );

    // Clean up previous round data
    if (roomData.rounds && roomData.rounds[roomData.currentRound]) {
      await updateDoc(doc(db, "rooms", roomDocId), {
        [`rounds.${roomData.currentRound}.submissions`]: deleteField(),
        [`rounds.${roomData.currentRound}.reviewVotes`]: deleteField(),
        [`rounds.${roomData.currentRound}.playersSubmitted`]: deleteField(),
      });
    }

    await updateDoc(doc(db, "rooms", roomDocId), {
      currentRound: round,
      currentLetter: letter,
      playedLetters: [...(roomData.playedLetters || []), letter],
      roundItemUsage: {}, // reset power-up usage for new round
      [`rounds.${round}`]: {
        letter: letter,
        startedAt: roundStartTime,
        timeLimit: timeLimit, // DELETE_FLAG: Ensure this is saved for Firebase functions
        isSpeedMode: isSpeedMode,
        submissions: [],
        isComplete: false,
        reviewCompleted: false,
        letterSource: source, // 'picked', 'auto', or 'random'
      },
    });

    const specialRound = (roomData.specialRounds || []).find((r) => r.round === round);
    analytics.track("round_started", {
      room_id: roomDocId,
      round_number: round,
      letter: letter,
      is_special_round: !!specialRound,
      ...(specialRound ? { special_round_type: specialRound.type } : {}),
    });

    // Brief delay then move to playing phase
    setTimeout(() => {
      setGamePhase("playing");
    }, 5000);
  };

  const handleReviewComplete = () => {
    setGamePhase("results");
  };

  const handleNextRound = () => {
    if (isHost) {
      if (roomData?.hasLetterPicker) {
        startNextRoundWithPicker();
      } else {
        startNextRound(); // Use existing random function
      }
    }
  };

  const endGame = async () => {
    if (!isHost || !roomDocId) return;

    const ok = await ask({
      title: "End the game for everyone?",
      message: "No more rounds start, and final scores are shown.",
      confirmLabel: "End game",
    });
    if (ok) {
      try {
        await updateDoc(doc(db, "rooms", roomDocId), {
          gameStatus: "ended",
          gameEndedAt: new Date(),
          gameEndedBy: currentUserId,
        });

        try {
          const hostName = localStorage.getItem("playerName");
          await systemMessageService.gameEndedByHost(roomDocId, hostName);
        } catch (error) {
          console.warn("Failed to send game end message:", error);
        }
        // Track game end in statistics
        // await statsService.trackGameEnded(
        //   roomData.roomId,
        //   roomData,
        //   false,
        //   currentUserId
        // );
        setGamePhase("finished");
      } catch (error) {
        console.error("Error ending game:", error);
      }
    }
  };

  const getFinalStandings = () => {
    if (!roomData?.rounds || !roomData?.players) return [];

    const playerTotals = {};

    // Initialize totals
    roomData.players.forEach((player) => {
      playerTotals[player.id] = {
        id: player.id,
        name: player.name,
        total: 0,
        roundScores: [],
      };
    });

    // Sum scores from all completed rounds
    Object.entries(roomData.rounds).forEach(([roundNum, round]) => {
      if (round.scores) {
        Object.entries(round.scores).forEach(([userId, categoryScores]) => {
          if (playerTotals[userId]) {
            const roundTotal = categoryScores.total || 0;
            playerTotals[userId].total += roundTotal;
            playerTotals[userId].roundScores.push({
              round: roundNum,
              letter: round.letter,
              score: roundTotal,
            });
          }
        });
      }
      // Include power-up cumulative adjustments (steal_points, share_fate)
      if (round.powerUpScores) {
        Object.entries(round.powerUpScores).forEach(([userId, adjustment]) => {
          if (playerTotals[userId]) {
            playerTotals[userId].total += adjustment || 0;
          }
        });
      }
    });

    return Object.values(playerTotals).sort((a, b) => b.total - a.total);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center">
        <div className="text-red-800 text-xl">Loading game...</div>
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center">
        <div className="text-red-800 text-xl">Game not found</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {confirmDialog}
      <div className="flex items-start space-x-2 justify-between bg-orange-50 w-full p-3 mb-2 lg:p-2 lg:mb-0 min-[1360px]:absolute min-[1360px]:left-0 min-[1360px]:top-0 min-[1360px]:z-20 min-[1360px]:w-auto min-[1360px]:p-3 min-[1360px]:bg-transparent">
        <HostControlPanel
          roomData={roomData}
          roomDocId={roomDocId}
          currentPhase={gamePhase}
        />
        {/* Non-hosts have no Settings button at all, so Leave Game stays
            here for them. For the host, it moved inside the Settings
            modal — showing both side by side here read as cluttered when
            everything else lives behind one Settings entry point. */}
        {!isHost && <LeaveGameButton roomData={roomData} roomDocId={roomDocId} />}
      </div>
      {isLetterPickingPhase && !showLetterPicker && (
        <div className="min-h-screen bg-orange-50 flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-2">
              <PencilMascot mood="thinking" size={88} label="Namey waiting for the letter" />
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-2">
              Letter Selection in Progress
            </h2>
            <p className="text-gray-600 mb-4">
              <span className="font-semibold">
                {currentLetterPicker?.playerName}
              </span>{" "}
              is choosing the letter for the next round...
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 text-sm">
                Please wait while they make their selection.
              </p>
            </div>

            {isHost && showHostSkipWaiting && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm mb-3">
                  <span className="font-semibold">
                    {currentLetterPicker?.playerName}
                  </span>{" "}
                  is taking too long...
                </p>
                <button
                  onClick={handleHostSkipFromWaiting}
                  className="bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 transition duration-300"
                >
                  Let Computer Choose
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DELETE_FLAG: Only show game phases when NOT in letter picking phase */}
      {!isLetterPickingPhase &&
        (() => {
          switch (gamePhase) {
            case "playing":
              return <PlayGame roomDocId={roomDocId} />;

            case "reviewing":
              return (
                <ReviewPhase
                  roomData={roomData}
                  roomDocId={roomDocId}
                  currentRound={roomData.currentRound}
                  onReviewComplete={handleReviewComplete}
                />
              );

            case "results":
              return (
                <div className="flex items-center justify-center flex-col">
                  <RoundResults
                    roomData={roomData}
                    roomDocId={roomDocId}
                    currentRound={roomData.currentRound}
                    onContinue={handleNextRound}
                    desktopActions={
                      isHost && roomData.gameStatus !== "ended" ? (
                        <div className="space-y-3">
                          <button
                            onClick={handleNextRound}
                            className="w-full bg-red-600 text-white font-bold text-lg px-8 py-3.5 rounded-xl hover:bg-red-700 transition duration-300"
                          >
                            Start Next Round
                          </button>
                          <button
                            onClick={endGame}
                            className="w-full bg-white border border-gray-300 text-gray-700 font-semibold px-8 py-3 rounded-xl hover:bg-gray-50 transition duration-300"
                          >
                            End Game
                          </button>
                        </div>
                      ) : !isHost ? (
                        <p className="text-center text-gray-600">
                          Waiting for host to start next round...
                        </p>
                      ) : null
                    }
                  />
                  <div className="text-center mb-8 lg:hidden">
                    {isHost && roomData.gameStatus !== "ended" && (
                      <div className="space-x-4">
                        <button
                          onClick={handleNextRound}
                          className="bg-red-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-red-700 transition duration-300"
                        >
                          Start Next Round
                        </button>
                        <button
                          onClick={endGame}
                          className="bg-gray-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-gray-700 transition duration-300"
                        >
                          End Game
                        </button>
                      </div>
                    )}
                    {!isHost && (
                      <p className="text-gray-600">
                        Waiting for host to start next round...
                      </p>
                    )}
                  </div>
                </div>
              );

            case "finished":
              return (
                <FinalResults
                  standings={getFinalStandings()}
                  roomData={roomData}
                  roomDocId={roomDocId}
                  currentUserId={currentUserId}
                />
              );

            default:
              return <PlayGame roomDocId={roomDocId} />;
          }
        })()}

      {/* 🔥 CHAT CONTAINER - ONLY SHOW ON SPECIFIC PHASES */}
      {(gamePhase === "reviewing" ||
        gamePhase === "results" ||
        gamePhase === "finished") && (
        <ChatContainer
          roomData={roomData}
          roomDocId={roomDocId}
          currentUserId={currentUserId}
          playerName={localStorage.getItem("playerName")}
          position="bottom-right"
          phaseOverride={gamePhase === "reviewing" ? "review" : gamePhase === "results" || gamePhase === "finished" ? "results" : "lobby"}
        />
      )}

      {/* DELETE_FLAG: Letter picker modal - show for the current picker */}
      <LetterPicker
        isOpen={showLetterPicker}
        onLetterSelected={handleLetterSelected}
        playedLetters={roomData?.playedLetters || []}
        currentRound={
          roomData?.nextRoundNumber || (roomData?.currentRound || 1) + 1
        }
        playerName={localStorage.getItem("playerName")}
        timeLimit={30}
        isHost={isHost}
        onHostSkip={handleLetterSelected}
        currentPickerName={currentLetterPicker?.playerName || ""}
      />
    </div>
  );
};

export default GameCoordinator;
